import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountabilityAPI } from '../../../services/accountabilityAPI';
import { PendingAppeal, Pagination } from '../../../types/accountability';
import {
  Table,
  Badge,
  Button,
  Card,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { format } from 'date-fns';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';

export const AppealsPage: React.FC = () => {
  const navigate = useNavigate();
  const [appeals, setAppeals] = useState<PendingAppeal[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [page, setPage] = useState(1);

  // Review modal
  const [reviewModal, setReviewModal] = useState<{
    open: boolean;
    type: 'approve' | 'deny' | null;
    violationId: number | null;
    notes: string;
  }>({
    open: false,
    type: null,
    violationId: null,
    notes: '',
  });

  useEffect(() => {
    loadAppeals();
  }, [page]);

  const loadAppeals = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountabilityAPI.getPendingAppeals(page, 25);

      if (response.success) {
        setAppeals(response.data || response.appeals || []);
        setPagination(response.pagination || null);
      }
    } catch (err: any) {
      setError(err.response?.data?.errors?.[0] || 'Failed to load appeals');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveAppeal = async () => {
    if (!reviewModal.violationId || !reviewModal.notes) return;

    try {
      const response = await accountabilityAPI.approveAppeal(
        reviewModal.violationId,
        reviewModal.notes,
      );

      if (response.success) {
        setSuccess('Appeal approved successfully');
        closeReviewModal();
        loadAppeals();
      }
    } catch (err: any) {
      setError(err.response?.data?.errors?.[0] || 'Failed to approve appeal');
    }
  };

  const handleDenyAppeal = async () => {
    if (!reviewModal.violationId || !reviewModal.notes) return;

    try {
      const response = await accountabilityAPI.denyAppeal(
        reviewModal.violationId,
        reviewModal.notes,
      );

      if (response.success) {
        setSuccess('Appeal denied');
        closeReviewModal();
        loadAppeals();
      }
    } catch (err: any) {
      setError(err.response?.data?.errors?.[0] || 'Failed to deny appeal');
    }
  };

  const openReviewModal = (type: 'approve' | 'deny', violationId: number) => {
    setReviewModal({
      open: true,
      type,
      violationId,
      notes: '',
    });
  };

  const closeReviewModal = () => {
    setReviewModal({
      open: false,
      type: null,
      violationId: null,
      notes: '',
    });
  };

  const handleSubmitReview = () => {
    if (reviewModal.type === 'approve') {
      handleApproveAppeal();
    } else if (reviewModal.type === 'deny') {
      handleDenyAppeal();
    }
  };

  const getViolationTypeBadge = (type: string) => {
    return type === 'late_drop' ? (
      <Badge color="warning">Late Drop</Badge>
    ) : (
      <Badge color="error">No Show</Badge>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Typography variant="h4">
          Worker Accountability - Pending Appeals
        </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Info Alert */}
        <Alert severity="info">
          Review worker appeals for violations. Approving an appeal will forgive
          the violation and may lift the suspension.
        </Alert>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : appeals.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              No pending appeals
            </Typography>
            <Typography variant="body2" color="textSecondary">
              All appeals have been reviewed
            </Typography>
          </Card>
        ) : (
          <Card>
            <Table>
              <thead>
                <tr>
                  <th>Worker</th>
                  <th>Violation Type</th>
                  <th>Occurred</th>
                  <th>Hours Before</th>
                  <th>Job ID</th>
                  <th>Appeal Reason</th>
                  <th>Submitted</th>
                  <th>Days Pending</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {appeals.map((appeal) => (
                  <tr key={appeal.violation_id}>
                    <td>
                      <Button
                        variant="text"
                        onClick={() => navigate(`/workers/${appeal.worker_id}`)}
                      >
                        {appeal.worker_name}
                      </Button>
                    </td>
                    <td>{getViolationTypeBadge(appeal.violation_type)}</td>
                    <td>
                      {format(new Date(appeal.occurred_at), 'MMM dd, yyyy')}
                    </td>
                    <td>{Number(appeal.hours_before_shift).toFixed(1)}h</td>
                    <td>
                      <Button
                        variant="text"
                        onClick={() => navigate(`/jobs/${appeal.job_id}`)}
                      >
                        #{appeal.job_id}
                      </Button>
                    </td>
                    <td>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={appeal.appeal_reason}
                      >
                        {appeal.appeal_reason}
                      </Typography>
                    </td>
                    <td>
                      {format(
                        new Date(appeal.appeal_submitted_at),
                        'MMM dd, yyyy',
                      )}
                    </td>
                    <td>
                      <Badge
                        color={appeal.days_pending > 3 ? 'error' : 'warning'}
                      >
                        {Math.floor(appeal.days_pending)} days
                      </Badge>
                    </td>
                    <td>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          color="success"
                          title="Approve Appeal"
                          onClick={() =>
                            openReviewModal('approve', appeal.violation_id)
                          }
                        >
                          <ThumbUpIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          title="Deny Appeal"
                          onClick={() =>
                            openReviewModal('deny', appeal.violation_id)
                          }
                        >
                          <ThumbDownIcon />
                        </IconButton>
                      </Stack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination */}
            {pagination && (
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2">
                  Showing{' '}
                  {(pagination.current_page - 1) * pagination.per_page + 1} to{' '}
                  {Math.min(
                    pagination.current_page * pagination.per_page,
                    pagination.total_count,
                  )}{' '}
                  of {pagination.total_count} appeals
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    disabled={pagination.current_page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </Button>
                  <Typography sx={{ px: 2, py: 1 }}>
                    Page {pagination.current_page} of {pagination.total_pages}
                  </Typography>
                  <Button
                    disabled={
                      pagination.current_page === pagination.total_pages
                    }
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </Button>
                </Stack>
              </Box>
            )}
          </Card>
        )}

        {/* Review Modal */}
        <Dialog
          open={reviewModal.open}
          onClose={closeReviewModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {reviewModal.type === 'approve' ? 'Approve Appeal' : 'Deny Appeal'}
          </DialogTitle>
          <DialogContent>
            <Alert
              severity={reviewModal.type === 'approve' ? 'success' : 'warning'}
              sx={{ mb: 2 }}
            >
              {reviewModal.type === 'approve'
                ? "Approving this appeal will forgive the violation and may lift the worker's suspension."
                : 'Denying this appeal will keep the violation active and the suspension in place.'}
            </Alert>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Admin Notes (required)"
              value={reviewModal.notes}
              onChange={(e) =>
                setReviewModal({ ...reviewModal, notes: e.target.value })
              }
              placeholder="Explain your decision..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeReviewModal}>Cancel</Button>
            <Button
              variant="contained"
              color={reviewModal.type === 'approve' ? 'success' : 'error'}
              onClick={handleSubmitReview}
              disabled={!reviewModal.notes}
            >
              {reviewModal.type === 'approve' ? 'Approve' : 'Deny'}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};
